import * as React from 'react'
import { EntityLocationDetails } from '../../EntityLocationDetails/EntityLocationDetails'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import { Measure } from '@user/components/DetailPages/sections/EntityName/MeasureAI/Measure'
import { useHandlers } from '../hooks'
import { useData } from './hooks'
import { ENTITY_TYPES, LocationDTO, useAppDispatch } from '@sales-center/common'
import LaunchIcon from '@mui/icons-material/Launch'
import { Theme } from '@mui/material/styles'
import Link from '@mui/material/Link'
import { EntityDetail, EntityLocationDTO } from '@user/store/map/typings'
import {
  fetchEntityDetailAndProducts,
  getRecordsAtLocation,
} from '@user/store/map/api'
import {
  setSelectedEntityVariant,
  setSelectedServiceLocationId,
} from '@user/store/map'
import Button from '@mui/material/Button'
import { LoadingButton } from '@sales-center/components'
import { AgreementFormPacketDialog } from '@user/components/Agreements/AgreementFormPacketDialog/AgreementFormPacketDialog'
import { CreateAgreementsDrawer } from '@user/components/Agreements/CreateAgreementDrawer/CreateAgreementDrawer'
import { CreateLocationDrawer } from '@user/components/Agreements/CreateLocationDrawer/CreateLocationDrawer'
import { ServiceDialog } from '@user/components/Services'
import { ActionButtons } from './ActionButtons/ActionButtons'
import { CardCapture } from './CardCapture'
import { Scheduling } from './Scheduling'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { StatusSection } from './atoms/StatusSection'
import { KnockStatusBox } from './atoms/KnockStatusBox'
import { DrawerContentErrorModal } from '@user/features/MapView/EntityDetailsDrawer/DrawerContentsSingle/DrawerContentErrorModal'

export type Props = {
  entityDetails: EntityDetail
  serviceLocation: LocationDTO
  variant: string
  entityStatus: 'empty' | 'loading' | 'error' | 'success'
  refetchEntityDetails: () => Promise<EntityDetail | null>
  onKnockStatusClick: () => void
  onViewKnockStatusHistory?: (location: EntityLocationDTO) => void
  onBackClick?: () => void
  onCloseDrawer?: () => void
  onRefresh?: () => void
  onRefreshPins?: () => void
  onReviewPendingLead?: (draftId: string) => void
}

function DrawerContentsSingle(props: Props) {
  const {
    entityDetails,
    serviceLocation,
    variant,
    refetchEntityDetails,
    onKnockStatusClick,
    onViewKnockStatusHistory,
    onCloseDrawer,
    onRefresh,
    onRefreshPins,
    onReviewPendingLead,
  } = props
  const dispatch = useAppDispatch()
  const isD2dEnabled = useD2DKnockEnabled()

  const isPendingLead = variant === 'pendinglead' || variant === 'draftlead'

  const {
    entityType,
    id,
    externalLocationId,
    servicesCount,
    referralSummary,
    branchId,
  } = entityDetails
  const {
    addressDisplay,
    measureAIEnabled,
    setTabIndex,
    tabIndex,
    hasValidAddress,
    nameAndCompanyDisplay,
    errorModal,
    setErrorModal,
    drawerOpen,
    formPacketEnabled,
    setDrawerOpen,
    onOpenSvc,
    onOpenSvcFromAgreement,
    cancelPopupOpen,
    handleCancel,
    hideCancelDialog,
    openCreateServiceDrawer,
    createLocationDrawerOpen,
    closeCreateServiceDrawer,
    deleteServiceWarningMessage,
    svcOpen,
    onCloseSvc,
    isAddingService,
    openedFromAgreement,
    minimumServicesCount,
    closeCreateDrawer,
  } = useData({ ...props })

  const handleLeadAutoClosed = React.useCallback(async () => {
    const { latitude, longitude } = serviceLocation

    if (latitude == null || longitude == null) {
      onCloseDrawer?.()
      onRefreshPins?.()
      return
    }

    const result = await dispatch(
      getRecordsAtLocation({ lat: latitude, lng: longitude }),
    )

    const opportunity = getRecordsAtLocation.fulfilled.match(result)
      ? result.payload.records.find(
          (record) =>
            record.entityType?.toLowerCase() ===
              ENTITY_TYPES.Opportunity.toLowerCase() && !!record.entityId,
        )
      : undefined

    if (opportunity?.entityId && opportunity.serviceLocationId != null) {
      dispatch(setSelectedEntityVariant('opportunity'))
      dispatch(setSelectedServiceLocationId(opportunity.serviceLocationId))
      await dispatch(
        fetchEntityDetailAndProducts([
          'opportunity',
          opportunity.entityId,
          opportunity.serviceLocationId,
          true,
        ]),
      )
    } else {
      onCloseDrawer?.()
    }

    onRefreshPins?.()
  }, [dispatch, serviceLocation, onCloseDrawer, onRefreshPins])

  const {
    handleDirectionsClick,
    handlePhoneClick,
    handleViewDetailClick,
    onOpenAgreementDrawer,
    isAgreementDrawerLoading,
    readOnly,
    canUpdate,
    isClosed,
  } = useHandlers({
    addressDisplay,
    serviceLocation,
    entityDetails,
    setErrorModal,
    setDrawerOpen,
    formPacketEnabled,
    onReviewPendingLead,
  })

  const entityTypeDisplay = React.useMemo(() => {
    switch (variant.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
      case 'pendinglead':
        return 'Pending Lead'
      default:
        return 'Unknown'
    }
  }, [variant])

  return (
    <>
      <Box
        sx={{
          marginLeft: 2,
          marginRight: 2,
          marginTop: !isD2dEnabled ? 1.2 : 0,
        }}
      >
        {isD2dEnabled && (
          <Typography variant="caption" mb={'12.5px'}>
            {entityTypeDisplay}
          </Typography>
        )}
        <Link
          onClick={() =>
            handleViewDetailClick(entityDetails?.id?.toString() ?? '', variant)
          }
          sx={{
            cursor: 'pointer',
          }}
        >
          <Typography variant="h6" sx={{ color: '#1565C0' }} mb={'12px'}>
            {nameAndCompanyDisplay}
          </Typography>
        </Link>
        {addressDisplay ? (
          <Link onClick={handleDirectionsClick}>
            <Typography
              component={'div'}
              variant="body2"
              sx={{
                color: '#1565C0',
                cursor: 'pointer',
                '&:hover': {
                  color: 'rgba(25, 112, 211, 0.9)',
                },
              }}
            >
              {addressDisplay}
              <LaunchIcon
                sx={{
                  marginLeft: (theme: Theme) => theme.spacing(0.5),
                  marginBottom: (theme: Theme) => theme.spacing(0.4),
                  fontSize: '20px',
                }}
              />
            </Typography>
          </Link>
        ) : null}
        <ActionButtons
          entityType={entityType}
          entityDetails={entityDetails}
          canUpdate={canUpdate}
          serviceLocation={serviceLocation}
          setErrorModal={setErrorModal}
        />
        <Stack display="flex" gap={1.2} mt={1}>
          {entityType !== ENTITY_TYPES.DraftLead ? (
            <LoadingButton
              onClick={onOpenAgreementDrawer}
              size="large"
              variant="contained"
              loading={isAgreementDrawerLoading}
              sx={{
                mt: 2.4,
              }}
            >
              Service / Agreement
            </LoadingButton>
          ) : null}
          {isPendingLead ? (
            <Button
              fullWidth
              size="large"
              variant="contained"
              sx={{ mt: 2.4 }}
              onClick={() =>
                handleViewDetailClick(
                  entityDetails?.id?.toString() ?? '',
                  variant,
                )
              }
            >
              Review pending lead
            </Button>
          ) : null}
          {isD2dEnabled ? (
            <Button
              fullWidth
              size="large"
              variant="contained"
              sx={{
                backgroundColor: '#D4E3F3',
                mt: 0,
              }}
              color="secondary"
              onClick={onKnockStatusClick}
            >
              Knock Status
            </Button>
          ) : null}
          {entityType !== ENTITY_TYPES.DraftLead ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <CardCapture
                entityType={entityType}
                entityDetails={entityDetails}
                canUpdate={canUpdate}
                isClosed={isClosed}
                serviceLocation={serviceLocation}
                setErrorModal={setErrorModal}
                refetchEntityDetails={refetchEntityDetails}
              />
              <Scheduling
                entityType={entityType}
                entityDetails={entityDetails}
                canUpdate={canUpdate}
                isClosed={isClosed}
                serviceLocation={serviceLocation}
                setErrorModal={setErrorModal}
                refetchEntityDetails={refetchEntityDetails}
                onLeadAutoClosed={handleLeadAutoClosed}
              />
            </Box>
          ) : null}
        </Stack>
        {isD2dEnabled ? (
          <KnockStatusBox
            entityDetails={entityDetails || null}
            variant={variant}
            serviceLocation={serviceLocation}
            canUpdate={canUpdate}
            onViewKnockStatusHistory={onViewKnockStatusHistory}
            onRefresh={onRefresh}
          />
        ) : (
          <StatusSection
            entityDetails={entityDetails || null}
            variant={variant}
            canUpdate={canUpdate}
            serviceLocation={serviceLocation}
          />
        )}
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 1.2, mt: '7px' }}>
        <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
          <Tab label="Overview" />
          {measureAIEnabled &&
            variant !== 'pendinglead' &&
            variant !== 'draftlead' && <Tab label={'Property Info'} />}
        </Tabs>
      </Box>
      <Box pt={2} bgcolor={'#f9fafb'}>
        {tabIndex === 0 && (
          <Stack
            direction="column"
            sx={{
              paddingLeft: 2,
              paddingRight: 2,
            }}
            spacing={0}
            mt={0}
          >
            <EntityLocationDetails
              defaultOpen
              entityDetails={entityDetails || null}
              phoneClick={handlePhoneClick}
              handleViewDetailClick={handleViewDetailClick}
              serviceLocation={serviceLocation}
              variant={variant}
              canUpdate={canUpdate}
            />
          </Stack>
        )}
        {tabIndex === 1 && hasValidAddress && (
          <Measure
            entityId={entityDetails?.id || 0}
            entityType={entityDetails?.entityType}
            locations={[serviceLocation]}
            variant="mapView"
            showBorder={false}
            iconColor={'rgba(9,27,42,0.56)'}
          />
        )}
      </Box>

      {errorModal.open && (
        <DrawerContentErrorModal
          entityId={id}
          entityType={entityType}
          errorModal={errorModal}
          setErrorModal={setErrorModal}
          onOpenSvc={onOpenSvc}
          refetchEntityDetails={refetchEntityDetails}
        />
      )}

      {formPacketEnabled ? (
        drawerOpen && (
          <AgreementFormPacketDialog
            entityId={id}
            entityType={entityType}
            onClose={closeCreateDrawer}
            onCloseFormManager={handleCancel}
            open={drawerOpen}
            openServicesDrawer={onOpenSvcFromAgreement}
            cancelPopupOpen={cancelPopupOpen}
            handleCancel={handleCancel}
            cancelPopupClose={hideCancelDialog}
            onLeadAutoClosed={handleLeadAutoClosed}
            source="mapview"
          />
        )
      ) : (
        <CreateAgreementsDrawer
          entityId={id}
          entityType={entityType}
          externalLocationId={externalLocationId}
          onClose={handleCancel}
          open={drawerOpen}
          openServicesDrawer={onOpenSvc}
          openCreateServiceDrawer={openCreateServiceDrawer}
          source="mapview"
        />
      )}

      {createLocationDrawerOpen && (
        <CreateLocationDrawer
          entityId={id}
          entityType={entityType}
          onClose={closeCreateServiceDrawer}
          open={createLocationDrawerOpen}
          externalLocationId={externalLocationId}
        />
      )}

      <ServiceDialog
        address={''}
        entityId={id}
        branchId={branchId}
        entityType={entityType}
        minimumServicesCount={minimumServicesCount}
        deleteServiceWarningMessage={deleteServiceWarningMessage}
        open={svcOpen}
        onClose={() => {
          onCloseSvc()
          refetchEntityDetails()
        }}
        openAddServiceDrawerOnMount={isAddingService}
        readOnly={readOnly}
        locationSource="existing"
        referralDiscountId={referralSummary?.referralDiscountId || undefined}
        totalServicesCount={servicesCount}
        closeButtonText={
          openedFromAgreement
            ? 'Back to Agreement'
            : 'Close Services and Products'
        }
      />
    </>
  )
}

export default DrawerContentsSingle
